import api from "api";
import { FormikErrors, FormikHelpers } from "formik";
import { ErrorResponse, processErrors } from "helpers/processErrors";
import capitaliseStringFirstLetter from "./capitaliseStringFirstLetter";
import { parseCamelCase } from "./parseCamelCase";
import { Dispatch } from "react";
import {
  setSubmitResponse,
  setSubmitResponseAndUrl,
  setSubmitResponseErrors,
  setSubmitResponseUrl,
} from "reducers/submitResponse";
import {
  ActionTypes,
  AutoSelectAllValuesFields,
  ExtraPayloadItems,
  OriginFormData,
  RelatedFormExtraDataToDisplayFields,
  RelatedFormSuccessResponseIds,
  resetRelatedFormReduxData,
  setRelatedFormOriginFormAutoSelectAllValuesFields,
  updateRelatedFormReduxData,
  updateRelatedFormSuccessResponseIds,
} from "reducers/listPage";
import { connectableObservableDescriptor } from "rxjs/internal/observable/ConnectableObservable";
import { CreatedDataSearchStringFieldNameData, NestedFormData } from "components/Entity/Entity";
import {
  CreatedDataParentFieldData,
  NestedFormModuleData,
  selectCreatedDataParentFieldData,
  setCreatedDataParentFieldData,
  setNestedFormModuleData,
} from "reducers/nestedCrudMenu";
import { APP_ROUTES } from "config";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ToastMessage, { toastConfig } from "./ToastMessage/ToastMessage";
// import ToastMessage from "components/ToastMessages/ToastMessage";

const submitSaveFormReduxData = (
  values: any,
  dispatch: Dispatch<any>,
  successResponseId?: number | null,
  payloadRelatedModuleName?: string,
  relatedFormOriginFormAutoSelectAllValuesFields?: AutoSelectAllValuesFields,
  idResponseFieldName?: string,
  relatedFormExtraDataToDisplayFields?: RelatedFormExtraDataToDisplayFields
) => {
  const pathName = window?.location?.pathname;

  if (values && idResponseFieldName) {
    const formData = values;
    const formId = values[idResponseFieldName];

    let actionType = ActionTypes.UPDATE;

    if (!formId || formId === 0) {
      actionType = ActionTypes.CREATE;
    }

    let moduleName = null;

    if (pathName) {
      moduleName = pathName.split("/")[1];
    }

    updateRelatedFormReduxData(formData, actionType, moduleName, dispatch);
  }

  if (
    successResponseId &&
    payloadRelatedModuleName &&
    relatedFormOriginFormAutoSelectAllValuesFields &&
    relatedFormOriginFormAutoSelectAllValuesFields.hasOwnProperty(payloadRelatedModuleName) &&
    Array.isArray(relatedFormOriginFormAutoSelectAllValuesFields[payloadRelatedModuleName]) &&
    relatedFormOriginFormAutoSelectAllValuesFields[payloadRelatedModuleName].length === 1 &&
    typeof relatedFormOriginFormAutoSelectAllValuesFields[payloadRelatedModuleName][0] ===
      "object" &&
    relatedFormOriginFormAutoSelectAllValuesFields[payloadRelatedModuleName][0].hasOwnProperty(
      "fieldName"
    )
  ) {
    const fieldName =
      relatedFormOriginFormAutoSelectAllValuesFields[payloadRelatedModuleName][0].fieldName;
    let tempIdData: RelatedFormSuccessResponseIds = {};

    let tempSubData: {
      [relatedFormFieldName: string]: {
        id: number;
        extraDataToDisplay?: string;
      }[];
    } = {};

    let additionalDataString = "";

    if (
      relatedFormExtraDataToDisplayFields &&
      relatedFormExtraDataToDisplayFields.hasOwnProperty(payloadRelatedModuleName)
    ) {
      const data = relatedFormExtraDataToDisplayFields[payloadRelatedModuleName];

      if (
        data &&
        typeof data === "object" &&
        data.hasOwnProperty(window.location.pathname) &&
        Array.isArray(data[window.location.pathname]) &&
        data[window.location.pathname].length > 0
      ) {
        const fields = data[window.location.pathname];

        if (fields && Array.isArray(fields) && fields.length > 0) {
          fields.forEach((item) => {
            const dataField = item.data;
            const dataSourceField = item.dataSource;

            const dataFieldValue =
              values && values.hasOwnProperty(dataField) ? values[dataField] : undefined;
            const dataSourceFieldValue =
              values && values.hasOwnProperty(dataSourceField)
                ? values[dataSourceField]
                : undefined;

            if (
              typeof dataFieldValue === "number" &&
              dataSourceFieldValue &&
              Array.isArray(dataSourceFieldValue) &&
              dataSourceFieldValue.length > 0
            ) {
              const matches = dataSourceFieldValue.filter((item) => item.value === dataFieldValue);

              if (
                Array.isArray(matches) &&
                matches.length === 1 &&
                typeof matches[0] == "object" &&
                matches[0].hasOwnProperty("label")
              ) {
                additionalDataString += ` - ${matches[0].label}`;
              }
            }
          });
        }
      }
    }

    tempSubData[fieldName] = [
      {
        id: successResponseId,
        extraDataToDisplay:
          additionalDataString && additionalDataString !== "" ? additionalDataString : undefined,
      },
    ];

    tempIdData[payloadRelatedModuleName] = tempSubData;

    dispatch(updateRelatedFormSuccessResponseIds(tempIdData));
  }
};

export const parseSubmitErrors = (
  err: any,
  dispatch: Dispatch<any>,
  helpers?: FormikHelpers<any>
) => {
  const error: any = err;

  if (
    error.response &&
    error.response.data &&
    error.response.data.errors &&
    Array.isArray(error.response.data.errors) &&
    error.response.data.errors.length > 0
  ) {
    let tempErrors: ErrorResponse[] = [];

    error.response.data.errors.forEach((item: any) => {
      if (typeof item === "object") {
        let fieldName: string | null = null;
        let messageCode: string | null = null;

        if (item.fieldName) {
          fieldName = item.fieldName;
        }
        if (item.messageCode) {
          messageCode = item.messageCode;
        }

        if (fieldName && messageCode) {
          const parsedMessageCode = capitaliseStringFirstLetter(
            messageCode.toLowerCase().replace(/_/g, " ")
          ).trim();

          tempErrors = [
            ...tempErrors,
            {
              fieldName,
              messageText: parsedMessageCode,
            },
          ];
        }
      }
    });

    if (helpers) {
      helpers.setErrors(processErrors(tempErrors));
    }

    const unparsedErrors = error.response.data.errors;

    let parsedErrors: any = {};
    unparsedErrors.forEach((item: any) => {
      if (
        typeof item === "object" &&
        item.hasOwnProperty("fieldName") &&
        item.hasOwnProperty("messageCode")
      ) {
        const parsedFieldName = parseCamelCase(item.fieldName)
          .split("_")
          .join(" ")
          .replace(/_/g, " ")
          .replaceAll(/  +/g, " ")
          .trim();
        const parsedMessageCode = item.messageCode
          .split("_")
          .join(" ")
          .replace(/_/g, " ")
          .replaceAll(/  +/g, " ")
          .trim();
        parsedErrors[parsedFieldName] = parsedMessageCode;
      }
    });

    if (Object.keys(parsedErrors).length > 0) {
      dispatch(setSubmitResponseErrors({ submitResponseErrors: parsedErrors }));
    }
  }
};

export function createSubmitForm(
  url: string,
  onDone: () => void,
  dispatch: Dispatch<any>,
  allRelatedFormExtraPayloadItems?: ExtraPayloadItems,
  relatedFormOriginFormAutoSelectAllValuesFields?: AutoSelectAllValuesFields,
  originFormData?: OriginFormData,
  relatedFormExtraDataToDisplayFields?: RelatedFormExtraDataToDisplayFields,
  saveSubmitResponse?: boolean,
  extraPayloadItems?: { [paramName: string]: any },
  nestedFormData?: NestedFormData,
  nestedFormModuleData?: NestedFormModuleData | null,
  createdDataSearchStringFieldName?: CreatedDataSearchStringFieldNameData,
  createdDataSearchIdFieldName?: CreatedDataSearchStringFieldNameData,
  nestedFormModuleName?: APP_ROUTES,
  existingCreatedDataParentFieldData?: CreatedDataParentFieldData,
  metadataDatasourceFields?: string[],
  handleResponse?: (response: any) => void
) {
  return async function submitForm<T>(values: T, helpers: FormikHelpers<T>) {
    try {
      let payload = values;

      const currentPathName = window.location.pathname;

      let payloadRelatedModuleName: string | undefined = undefined;

      let idResponseFieldName: string | undefined = undefined;

      if (
        allRelatedFormExtraPayloadItems &&
        Object.keys(allRelatedFormExtraPayloadItems).length > 0 &&
        allRelatedFormExtraPayloadItems.hasOwnProperty(currentPathName)
      ) {
        const payloadItemsData = allRelatedFormExtraPayloadItems[currentPathName];

        payloadRelatedModuleName = payloadItemsData
          ? payloadItemsData.relatedModuleName
          : undefined;

        const payloadRelatedParams = payloadItemsData ? payloadItemsData.param : null;

        idResponseFieldName = payloadItemsData ? payloadItemsData.idResponseFieldName : undefined;

        payload = { ...payload, ...payloadRelatedParams };
      }

      if (!originFormData && extraPayloadItems) {
        payload = { ...payload, ...extraPayloadItems };
      }

      const response = await api.post(url, payload);

      if (response.status === 200 && response.data && response.data.status === `1`) {
        helpers.setSubmitting(false);

        if (handleResponse) {
          handleResponse(response.data);
        }

        dispatch(setSubmitResponseErrors({ submitResponseErrors: null }));

        if (
          nestedFormModuleName &&
          nestedFormModuleData &&
          createdDataSearchStringFieldName &&
          createdDataSearchStringFieldName.hasOwnProperty(nestedFormModuleName) &&
          createdDataSearchIdFieldName &&
          createdDataSearchIdFieldName.hasOwnProperty(nestedFormModuleName) &&
          payload &&
          typeof payload == "object"
        ) {
          const fieldName =
            createdDataSearchStringFieldName[
              nestedFormModuleName as keyof typeof createdDataSearchStringFieldName
            ];
          const idFieldName =
            createdDataSearchIdFieldName[
              nestedFormModuleName as keyof typeof createdDataSearchIdFieldName
            ];

          let valueToSearchInParentForm: any = null;

          if (
            fieldName &&
            payload.hasOwnProperty(fieldName) &&
            payload[fieldName as keyof typeof payload] !== null &&
            nestedFormModuleData.createdDataParentFieldName
          ) {
            valueToSearchInParentForm = payload[fieldName as keyof typeof payload];
          } else if (
            fieldName &&
            response.data.details &&
            typeof response.data.details === "object" &&
            response.data.details.hasOwnProperty(fieldName)
          ) {
            // Also check response
            valueToSearchInParentForm =
              response.data.details[fieldName as keyof typeof response.data.details];
          }

          if (
            fieldName &&
            nestedFormModuleData.createdDataParentFieldName &&
            valueToSearchInParentForm
          ) {
            const parentFormModuleName = window.location.pathname;

            let createdDataParentFieldDataId: string | undefined = undefined;

            if (
              response.data.details &&
              typeof response.data.details === "object" &&
              response.data.details.hasOwnProperty(idFieldName)
            ) {
              createdDataParentFieldDataId = `${
                response.data.details[idFieldName as keyof typeof response.data.details]
              }`;
            }

            const existingModuleData =
              existingCreatedDataParentFieldData &&
              existingCreatedDataParentFieldData.hasOwnProperty(parentFormModuleName)
                ? existingCreatedDataParentFieldData[
                    parentFormModuleName as keyof typeof existingCreatedDataParentFieldData
                  ]
                : undefined;

            let newCreatedDataParentFieldData: CreatedDataParentFieldData =
              existingCreatedDataParentFieldData ? { ...existingCreatedDataParentFieldData } : {};

            if (existingModuleData) {
              newCreatedDataParentFieldData[
                parentFormModuleName as keyof typeof newCreatedDataParentFieldData
              ] = [
                ...existingModuleData,
                {
                  fieldName: nestedFormModuleData.createdDataParentFieldName,
                  searchString: valueToSearchInParentForm,
                  id: createdDataParentFieldDataId ?? undefined,
                },
              ];
            } else {
              newCreatedDataParentFieldData[
                parentFormModuleName as keyof typeof newCreatedDataParentFieldData
              ] = [
                {
                  fieldName: nestedFormModuleData.createdDataParentFieldName,
                  searchString: valueToSearchInParentForm,
                  id: createdDataParentFieldDataId ?? undefined,
                },
              ];
            }

            dispatch(setCreatedDataParentFieldData(newCreatedDataParentFieldData));
          }
        }

        if (saveSubmitResponse && response.data.details) {
          dispatch(
            setSubmitResponseAndUrl({
              response: response.data.details,
              url: url,
            })
          );
        }

        let responseId: number | null = null;

        if (
          response &&
          response.data &&
          response.data.details &&
          idResponseFieldName &&
          typeof response.data.details === "object" &&
          response.data.details.hasOwnProperty(idResponseFieldName)
        ) {
          responseId = response.data.details[idResponseFieldName];
        }
        submitSaveFormReduxData(
          values,
          dispatch,
          responseId,
          payloadRelatedModuleName,
          relatedFormOriginFormAutoSelectAllValuesFields,
          idResponseFieldName,
          relatedFormExtraDataToDisplayFields
        );

        if (originFormData) {
          dispatch(
            setRelatedFormOriginFormAutoSelectAllValuesFields(
              relatedFormOriginFormAutoSelectAllValuesFields ?? null
            )
          );
        }

        if (originFormData && currentPathName === originFormData.pathName) {
          dispatch(resetRelatedFormReduxData(dispatch, onDone));
        } else {
          onDone();
        }
      } else {
        const { errors } = response.data;
        if (errors.length > 0) {
          helpers.setSubmitting(false);
          helpers.setErrors(processErrors(errors));
        }
      }
    } catch (err) {
      parseSubmitErrors(err, dispatch, helpers);
      handleToastError(err);
      // throw err;
    } finally {
      helpers.setSubmitting(false);
    }
  };
}

export function createMultipartSubmitForm(
  url: string,
  onDone: () => void,
  dispatch: Dispatch<any>,
  additionalValues: [string, any][],
  // params?: any,
  allRelatedFormExtraPayloadItems?: ExtraPayloadItems,
  relatedFormOriginFormAutoSelectAllValuesFields?: AutoSelectAllValuesFields,
  originFormData?: OriginFormData,
  relatedFormExtraDataToDisplayFields?: RelatedFormExtraDataToDisplayFields,
  saveSubmitResponse?: boolean,
  extraPayloadItems?: { [paramName: string]: any },
  nestedFormData?: NestedFormData,
  nestedFormModuleData?: NestedFormModuleData | null,
  createdDataSearchStringFieldName?: CreatedDataSearchStringFieldNameData,
  createdDataSearchIdFieldName?: CreatedDataSearchStringFieldNameData,
  nestedFormModuleName?: APP_ROUTES,
  existingCreatedDataParentFieldData?: CreatedDataParentFieldData,
  metadataDatasourceFields?: string[],
  setProgress?: (progress: number) => void
) {
  return async function submitForm<T>(values: T, helpers: FormikHelpers<T>) {
    try {
      const formData = new FormData();
      const config = {
        headers: {
          "content-type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: any) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (setProgress) setProgress(progress);
        },
      };

      let postValues = values;

      for (const [key, value] of [
        // @ts-ignore
        ...Object.entries(postValues),
        ...additionalValues,
      ]) {
        let datasourceField = false;

        if (metadataDatasourceFields && metadataDatasourceFields.includes(key)) {
          datasourceField = true;
        }

        if (value != null && !datasourceField) {
          if (value.name) {
            formData.append(key, value, (value as File)?.name ?? undefined);
          } else if (typeof value !== "object") {
            formData.append(key, String(value));
          } else if (value instanceof Date) {
            formData.append(key, new Date(value).toISOString());
          } else if (typeof value === "object" && key === "itemDimensions") {
            Object.keys(value).forEach((key2: string) => {
              formData.append(`${key}.${key2}`, value[key2]);
            });
          } else if (
            typeof value === "object" &&
            (key === "programmesIDs" || key === "projectsIDs" || key === "sitesIDs")
          ) {
            Object.keys(value).forEach((key2: string) => {
              formData.append(`${key}`, value[key2]);
            });
          } else if (typeof value === "object" && key == "files") {
            Object.keys(value).forEach((key2: string) => {
              const v = value[key2];

              if (v && v.file && v.file.name) {
                formData.append(
                  `${key}`,

                  v.file,
                  (v.file as File)?.name ?? undefined
                );
              }
            });
          } else if (Array.isArray(value) || typeof value === "object") {
            const mappedData = mapObjectToFormData(value, key, {});
            mappedData &&
              Object.entries(mappedData).forEach(([key, value]) => {
                if (value !== null && value !== undefined) formData.append(key, value);
              });
          }
        }
      }

      const currentPathName = window.location.pathname;

      let payloadRelatedModuleName: string | undefined = undefined;

      let idResponseFieldName: string | undefined = undefined;

      if (
        allRelatedFormExtraPayloadItems &&
        Object.keys(allRelatedFormExtraPayloadItems).length > 0 &&
        allRelatedFormExtraPayloadItems.hasOwnProperty(currentPathName)
      ) {
        const payloadItems = allRelatedFormExtraPayloadItems[currentPathName].param;

        idResponseFieldName =
          allRelatedFormExtraPayloadItems[currentPathName].relatedModuleName ?? undefined;

        payloadRelatedModuleName = allRelatedFormExtraPayloadItems[currentPathName]
          .relatedModuleName
          ? allRelatedFormExtraPayloadItems[currentPathName].relatedModuleName
          : undefined;

        Object.keys(payloadItems).forEach((key) => {
          const value = payloadItems[key];

          formData.append(key, String(value));
        });
      }

      if (!originFormData && extraPayloadItems) {
        Object.keys(extraPayloadItems).forEach((key) => {
          formData.append(key, String(extraPayloadItems[key]));
        });
      }

      const response = await api.post(url, formData, config);

      if (response.status === 200 && response.data && response.data.status === `1`) {
        helpers.setSubmitting(false);

        dispatch(setSubmitResponseErrors({ submitResponseErrors: null }));

        if (
          nestedFormModuleName &&
          nestedFormModuleData &&
          createdDataSearchStringFieldName &&
          createdDataSearchStringFieldName.hasOwnProperty(nestedFormModuleName) &&
          createdDataSearchIdFieldName &&
          createdDataSearchIdFieldName.hasOwnProperty(nestedFormModuleName) &&
          values &&
          typeof values == "object"
        ) {
          const fieldName =
            createdDataSearchStringFieldName[
              nestedFormModuleName as keyof typeof createdDataSearchStringFieldName
            ];
          const idFieldName =
            createdDataSearchIdFieldName[
              nestedFormModuleName as keyof typeof createdDataSearchIdFieldName
            ];

          if (
            fieldName &&
            values.hasOwnProperty(fieldName) &&
            nestedFormModuleData.createdDataParentFieldName
          ) {
            const valueToSearchInParentForm: any = values[fieldName as keyof typeof values];

            const parentFormModuleName = window.location.pathname;

            let createdDataParentFieldDataId: string | undefined = undefined;

            if (
              response.data.details &&
              typeof response.data.details === "object" &&
              response.data.details.hasOwnProperty(idFieldName)
            ) {
              createdDataParentFieldDataId = `${
                response.data.details[idFieldName as keyof typeof response.data.details]
              }`;
            }

            const existingModuleData =
              existingCreatedDataParentFieldData &&
              existingCreatedDataParentFieldData.hasOwnProperty(parentFormModuleName)
                ? existingCreatedDataParentFieldData[
                    parentFormModuleName as keyof typeof existingCreatedDataParentFieldData
                  ]
                : undefined;

            let newCreatedDataParentFieldData: CreatedDataParentFieldData =
              existingCreatedDataParentFieldData ? { ...existingCreatedDataParentFieldData } : {};

            if (existingModuleData) {
              newCreatedDataParentFieldData[
                parentFormModuleName as keyof typeof newCreatedDataParentFieldData
              ] = [
                ...existingModuleData,
                {
                  fieldName: nestedFormModuleData.createdDataParentFieldName,
                  searchString: valueToSearchInParentForm,
                  id: createdDataParentFieldDataId ?? undefined,
                },
              ];
            } else {
              newCreatedDataParentFieldData[
                parentFormModuleName as keyof typeof newCreatedDataParentFieldData
              ] = [
                {
                  fieldName: nestedFormModuleData.createdDataParentFieldName,
                  searchString: valueToSearchInParentForm,
                  id: createdDataParentFieldDataId ?? undefined,
                },
              ];
            }

            dispatch(setCreatedDataParentFieldData(newCreatedDataParentFieldData));
          }
        }

        if (saveSubmitResponse && response.data.details) {
          dispatch(
            setSubmitResponseAndUrl({
              response: response.data.details,
              url: url,
            })
          );
        }

        let responseId: number | null = null;

        if (
          response &&
          response.data &&
          response.data.details &&
          idResponseFieldName &&
          typeof response.data.details === "object" &&
          response.data.details.hasOwnProperty(idResponseFieldName)
        ) {
          responseId = response.data.details[idResponseFieldName];
        }
        submitSaveFormReduxData(
          values,
          dispatch,
          responseId,
          payloadRelatedModuleName,
          relatedFormOriginFormAutoSelectAllValuesFields,
          idResponseFieldName,
          relatedFormExtraDataToDisplayFields
        );

        if (originFormData) {
          dispatch(
            setRelatedFormOriginFormAutoSelectAllValuesFields(
              relatedFormOriginFormAutoSelectAllValuesFields ?? null
            )
          );
        }

        // Delete redux data if this is the submit function of the origin form
        if (originFormData && currentPathName === originFormData.pathName) {
          dispatch(resetRelatedFormReduxData(dispatch, onDone));
        } else {
          onDone();
        }
      } else {
        const { errors } = response.data;
        if (errors.length > 0) {
          helpers.setSubmitting(false);
          helpers.setErrors(processErrors(errors));
        }
      }
    } catch (err) {
      parseSubmitErrors(err, dispatch, helpers);
      handleToastError(err);
      // throw err;
    } finally {
      helpers.setSubmitting(false);
    }
  };
}

const handleToastError = (err: unknown) => {
  const errRes = (err as any)?.response as any;

  toast(
    ToastMessage({
      toastData: [
        {
          title: "Error",
          text:
            err &&
            errRes &&
            errRes.data &&
            errRes.data.errors &&
            Array.isArray(errRes.data.errors) &&
            errRes.data.errors?.length > 0
              ? errRes.data.errors[0]?.messageCode
              : "Failed to submit - please try again later",
        },
      ],
      className: "ToastError",
    }),
    toastConfig
  );
};

export const mapObjectToFormData = (
  value: any,
  prevKey: string,
  results: { [k: string]: any } = {}
): { [k: string]: any } | void => {
  if (value == null || value instanceof File || typeof value !== "object") {
    results[prevKey] = value;
    return;
  }

  Object.keys(value).forEach((key) => {
    mapObjectToFormData(value[key], `${prevKey}[${key}]`, results);
  });
  return results;
};
